import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { suggestAttachServiceRequests } from '../../../../../api'
import { NOTIFICATION_TYPE } from '../../../../../constants/common'
import { useInitData } from '../../../../../hooks'
import { getAIGretaErrorNotificationText } from '../../../../AIGreta'
import { ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID } from '../../AttachServiceRequestsModal.constants'

import {
  getAISuggestAvailableSuggestions,
  getAISuggestSelectionItem,
} from './useAttachServiceRequestsAISuggest.utils'

export function useAttachServiceRequestsAISuggest({
  emailMessageUuid,
  excludeIds,
  selected,
  tableItems,
  onPrependNotification,
  onRemoveNotification,
  onReplaceSelection,
  onUnselectAll,
}) {
  const { t } = useTranslation()

  const { ai_features: aiFeatures } = useInitData()

  const [suggestions, setSuggestions] = useState(null)
  const [isSuggestLoading, setSuggestLoading] = useState(false)
  const [isCollapsed, setCollapsed] = useState(false)
  const [aiSelectedRequestId, setAiSelectedRequestId] = useState(null)

  const isAIAvailable =
    Boolean(emailMessageUuid) && aiFeatures?.request_suggestion

  function handleClearAISelection() {
    if (aiSelectedRequestId != null) {
      if (selected[0]?.id === aiSelectedRequestId) {
        onUnselectAll()
      }

      setAiSelectedRequestId(null)
    }
  }

  function handleDropAISelectionMark() {
    setAiSelectedRequestId(null)
  }

  async function handleRequestSuggestions() {
    onRemoveNotification(ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID.aiSuggest)
    handleClearAISelection()
    setSuggestions(null)
    setSuggestLoading(true)

    return suggestAttachServiceRequests({ email_message: emailMessageUuid })
      .then(({ data }) => {
        const nextSuggestions = getAISuggestAvailableSuggestions({
          excludeIds,
          suggestions: data?.suggestions,
        })

        if (nextSuggestions.length > 0) {
          setSuggestions(nextSuggestions)
          setCollapsed(false)
          setAiSelectedRequestId(nextSuggestions[0].request_id)
          onReplaceSelection(
            getAISuggestSelectionItem({
              suggestion: nextSuggestions[0],
              tableItems,
            })
          )
        } else {
          onPrependNotification({
            id: ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID.aiSuggest,
            text: t('Modal.AttachServiceRequestsAINoMatchKey'),
            type: NOTIFICATION_TYPE.warning,
          })
        }
      })
      .catch(error => {
        onPrependNotification({
          id: ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID.aiSuggest,
          text: getAIGretaErrorNotificationText(error, t),
          type: NOTIFICATION_TYPE.error,
        })
      })
      .finally(() => {
        setSuggestLoading(false)
      })
  }

  function handleSelectSuggestion(suggestion, isChecked) {
    handleDropAISelectionMark()

    if (isChecked) {
      onReplaceSelection(getAISuggestSelectionItem({ suggestion, tableItems }))
    } else {
      onUnselectAll()
    }
  }

  function handleCollapseToggle() {
    setCollapsed(prevCollapsed => !prevCollapsed)
  }

  return {
    handleClearAISelection,
    handleCollapseToggle,
    handleDropAISelectionMark,
    handleRequestSuggestions,
    handleSelectSuggestion,
    isAIAvailable,
    isCollapsed,
    isSuggestLoading,
    suggestions,
  }
}
